// The home page behaves like a tv channel: when a bout ends, the stage's own ending is the
// clock — the loser falls, the winner bows and turns to leave, and the clouds say so — then the
// next bout is swapped in place under the same stage, continuously (fetch, no fade, no reload):
// the next pair walk on as the last winner walks off. Replays roll the same way.
import { resumeFollow } from "./follow.ts";
import { swapBout, swapPanel } from "./page.ts";
import { T } from "./timing.ts";
import { parseRingStatus } from "./wire.ts";
import { clearFx } from "./fx/canvas.ts";
import { isBroadcast } from "./consent.ts";

export const isTv = () => location.pathname === "/";

/** Everything the current ring owns, so a switch can tear it down. */
class Ring {
  streams: EventSource[];
  timers: (() => void)[];
  constructor() {
    this.streams = [];
    this.timers = [];
  }
  stream(es: EventSource) {
    this.streams.push(es);
  }
  /** a teardown step: clear a timer, cancel an animation */
  onTeardown(fn: () => void) {
    this.timers.push(fn);
  }
  /** the streams and the timers go; the stage too, unless it is to stay (a channel change: the world stands, the next pair walk on as the winner walks off) */
  teardown(keepStage: boolean = false) {
    this.streams.forEach((e) => e.close());
    this.timers.forEach((f) => f());
    this.streams = [];
    this.timers = [];
    if (!keepStage) clearFx();
  }
}
export const ring = new Ring();

/** After this many failed channel changes in a row, go to the front door the hard way.
 * Generous on purpose: a transient burst of failed fetches (a server hiccup, a dropped
 * keep-alive) should never strand the tv on a sealed bout. */
const SWITCH_GIVE_UP = 8;

export function makeTv(initRing: () => void) {
  /** the one global stream: `started` whenever a bout opens. the latest `onStart` handler wins */
  let es: EventSource | null = null;
  let onStarted: (() => void) | null = null;
  let t: ReturnType<typeof setTimeout> | 0 = 0;
  let failures = 0;
  const listen = () => {
    if (es || !window.EventSource) return;
    es = new EventSource("/events");
    es.addEventListener("started", () => {
      if (onStarted) onStarted();
    });
    es.onerror = () => {
      /* the browser retries */
    };
  };
  const tv = {
    /** leaving the ring (a page change): everything it owns is torn down */
    leave() {
      clearTimeout(t);
      if (es) {
        es.close();
        es = null;
      }
      ring.teardown();
    },
    onStart(fn: () => void) {
      onStarted = fn;
      listen();
    },
    /**
     * The bout is over. Once the stage is clear (the loser down, the winner leaving), change channel the moment the next
     * bout is on: the `started` event, or the ring status says so. A replay does not wait at all: the next bout (live if
     * one is on, else another replay). Nothing after END_FALLBACK: go anyway.
     */
    next(hold: number) {
      clearTimeout(t);
      const bout = document.querySelector("article.bout") as HTMLElement | null;
      const cur = bout ? +(bout.dataset as BoutDataset).bout : null;
      const replay = !!document.querySelector("article.bout[data-replay]");
      let started = false;
      let waited = 0; // past the hold, in polls
      tv.onStart(() => {
        started = true;
      });
      // the clock: the stage's own ending (basho:cleared, from the clouds) rather than a fixed hold; the hold twice over is the fallback should it never come
      let cleared = false;
      const onCleared = (linger = 0) => {
        if (cleared) return;
        cleared = true;
        clearTimeout(t);
        t = setTimeout(look, linger);
      };
      // a crowning lingers past the stage's ending as it does on the flat ring: what its hold has over a plain one
      if (bout) bout.addEventListener("basho:cleared", () => onCleared(Math.max(0, hold - T.END_HOLD)), { once: true });
      const look = async () => {
        if (started || replay || waited > T.END_FALLBACK) {
          tv.switchNow();
          return;
        }
        try {
          const status = parseRingStatus(await (await fetch("/ring/status", { cache: "no-store" })).json());
          if (status && status.live !== null && status.live !== cur) {
            tv.switchNow();
            return;
          }
        } catch (e) {
          console.warn("ring status", e);
        }
        waited += T.NEXT_POLL;
        t = setTimeout(look, T.NEXT_POLL);
      };
      // the stage says when; the hold twice over failing that. Without a stage (the classic ring) there is no fall to wait for: the hold once, to read the verdict
      t = setTimeout(() => onCleared(), bout?.classList.contains("staged") ? hold * 2 : hold);
    },
    async switchNow() {
      clearTimeout(t);
      // tell the server which bout we just watched, so the next pick is a different one
      const seen = (document.querySelector("article.bout") as HTMLElement | null)?.dataset.bout;
      const bcast = isBroadcast();
      const url = seen ? `/?not=${seen}${bcast ? "&broadcast=1" : ""}` : bcast ? "/?broadcast=1" : "/";
      try {
        const { doc } = await swapBout(url, { onBlank: () => ring.teardown(true) }); // in place under the stage, which stands: the next pair walk on as the winner walks off
        swapPanel(doc); // and the side of the broadcast with it: the banzuke the bout just moved, the recent bouts, the queue, the stats (#557)
        failures = 0;
        resumeFollow();
        initRing();
      } catch (e) {
        console.warn("channel change failed (" + (failures + 1) + "/" + SWITCH_GIVE_UP + ")", e);
        ring.teardown(true);
        if (++failures >= SWITCH_GIVE_UP) {
          location.href = bcast ? "/?broadcast=1" : "/";
          return;
        }
        t = setTimeout(() => tv.switchNow(), T.NEXT_POLL);
      }
    },
  };
  return tv;
}
